/**
 * Page de paramètres de l'établissement
 *
 * 🔒 ACCÈS : Réservé aux Admin (et Super Admin).
 * La garde est appliquée au niveau du routeur via <ProtectedRoute requireAdmin>
 * dans src/App.tsx. Un Staff qui tape l'URL est redirigé vers /dashboard.
 *
 * ⚠️ SYNCHRONISATION ONBOARDING
 * 
 * Les hash des onglets (tabs) de cette page sont référencés dans :
 * - src/config/onboardingConfig.ts (ESTABLISHMENT_TABS)
 * 
 * Si vous modifiez les valeurs des TabsTrigger (value="info", etc.),
 * vous DEVEZ mettre à jour onboardingConfig.ts pour que les liens
 * du widget d'onboarding restent fonctionnels.
 * 
 * Onglets actuels : booking, config, info, reviews, data
 * 
 * @see docs/ONBOARDING_CONFIGURATION.md
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Building, Mail, Phone, MapPin, Globe, Loader2, Link2, Copy, Check, ExternalLink, Star, HelpCircle, MessageSquare, Code, Users, Settings, Trash2, Download, ShieldAlert, Calendar, XCircle, Database, AlertTriangle, Minus, Plus, Info, Megaphone, Heading } from 'lucide-react';
import { PlanBadge } from '@/components/PlanBadge';
import { PiktableLoader } from '@/components/PiktableLoader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useEstablishmentSettings, EstablishmentSettingsUpdate } from '@/hooks/useEstablishmentSettings';
import { useReservationSettings } from '@/hooks/useReservationSettings';
import { useSupabaseData } from '@/hooks/useSupabaseData';
import { useAuth } from '@/contexts/AuthContext';
import { formatCustomerName } from '@/lib/clientUtils';
import { toast } from '@/hooks/use-toast';
import WidgetCodeGenerator from '@/components/WidgetCodeGenerator';
import { BookingModeSettings } from '@/components/BookingModeSettings';
import { TimeSlotLimitsSettings } from '@/components/TimeSlotLimitsSettings';
import { ChildrenSettings } from '@/components/ChildrenSettings';
import { DeleteAllDataDialog } from '@/components/DeleteAllDataDialog';
import { DisableTerraceDialog } from '@/components/DisableTerraceDialog';
import { ZoneManagementCard } from '@/components/ZoneManagementCard';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { exportReservationsToCSV, ReservationExportData } from '@/lib/csvExportUtils';
import { cn } from '@/lib/utils';
import { GoogleReviewLinkHelper } from '@/components/GoogleReviewLinkHelper';
import { EstablishmentNoticesSettings } from '@/components/EstablishmentNoticesSettings';
import { EstablishmentQuestionsSettings } from '@/components/EstablishmentQuestionsSettings';
import { MAX_BOOKING_TITLE_LENGTH, resolveBookingTitle } from '@/lib/bookingTitleUtils';

// Review platform configuration - Google is the main one
const GOOGLE_PLATFORM = { 
  id: 'google', 
  name: 'Google Business', 
  field: 'googleBusinessUrl' as const,
  icon: '🔵',
  helpText: 'Voir le guide détaillé ci-dessous pour obtenir votre lien.',
  showDetailedHelp: true
};

const OTHER_REVIEW_PLATFORMS = [
  { 
    id: 'tripadvisor', 
    name: 'TripAdvisor', 
    field: 'tripadvisorReviewUrl' as const,
    icon: '🦉',
    helpText: 'Copiez l\'URL de votre page TripAdvisor ou utilisez le Management Center pour obtenir un lien de partage.'
  },
  { 
    id: 'yelp', 
    name: 'Yelp', 
    field: 'yelpReviewUrl' as const,
    icon: '🔴',
    helpText: 'Format : yelp.com/writeareview/biz/nom-de-votre-restaurant'
  },
  { 
    id: 'facebook', 
    name: 'Facebook', 
    field: 'facebookReviewUrl' as const,
    icon: '📘',
    helpText: 'Utilisez l\'URL de votre page Facebook suivie de /reviews'
  },
  { 
    id: 'trustpilot', 
    name: 'Trustpilot', 
    field: 'trustpilotReviewUrl' as const,
    icon: '⭐',
    helpText: 'Format : trustpilot.com/evaluate/votre-domaine.com'
  },
  { 
    id: 'thefork', 
    name: 'TheFork / LaFourchette', 
    field: 'theforkReviewUrl' as const,
    icon: '🍴',
    helpText: 'Copiez l\'URL de votre page restaurant sur TheFork ou LaFourchette.'
  },
];

const ALL_REVIEW_PLATFORMS = [GOOGLE_PLATFORM, ...OTHER_REVIEW_PLATFORMS];

export default function Establishment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, establishmentId, session } = useAuth();
  const { settings, isLoading, updateSettings, isUpdating } = useEstablishmentSettings(establishmentId);
  const {
    settings: reservationSettings,
    loading: reservationSettingsLoading,
    togglePause,
    updateBookingRange,
    updateAllowSameDayCancellation,
    updatePauseWithMessage,
    updatePeopleLimits,
    updateMinBookingMinutes,
    updateBookingMode,
    updateLargeGroupEmail,
    updateLargeGroupContact,
    updateChildrenSettings,
    updateStrollerSettings,
    updateSameDayCutoffTime,
    updateTerraceSettings,
    updateMarketingSettings,
    updateZonesSettings,
    updateBlockedActionsContact,
    updateRequirePhone,
    updateBookingTitle,
    refetch: refetchReservationSettings
  } = useReservationSettings({ establishmentId: establishmentId || undefined });
  const { 
    tables: supabaseTables, 
    combinedTables: supabaseCombinedTables, 
    reservations: supabaseReservations,
    timeSlots: supabaseTimeSlots,
    loading: dataLoading 
  } = useSupabaseData({ useAuthenticatedData: true, establishmentId: establishmentId || undefined, session });
  
  const [activeTab, setActiveTab] = useState('booking');
  const [linkCopied, setLinkCopied] = useState(false);
  const [minPeople, setMinPeople] = useState<number>(1);
  const [maxPeople, setMaxPeople] = useState<number>(20);
  const [minDays, setMinDays] = useState<number>(2);
  const [maxDays, setMaxDays] = useState<number>(30);
  const [minMinutes, setMinMinutes] = useState<number>(0);
  const [sameDayCutoffTime, setSameDayCutoffTime] = useState<string | null>(null);
  const [sameDayMode, setSameDayMode] = useState<'delay' | 'cutoff'>('delay');
  const [allowSameDayCancellation, setAllowSameDayCancellation] = useState<boolean>(true);
  const [showPhoneOnBlocked, setShowPhoneOnBlocked] = useState<boolean>(false);
  const [showEmailOnBlocked, setShowEmailOnBlocked] = useState<boolean>(false);
  const [blockedActionsPhoneInput, setBlockedActionsPhoneInput] = useState<string>('');
  const [blockedActionsEmailInput, setBlockedActionsEmailInput] = useState<string>('');
  const [peopleLimitsUpdating, setPeopleLimitsUpdating] = useState(false);
  const [requirePhoneUpdating, setRequirePhoneUpdating] = useState(false);
  const [bookingTitleInput, setBookingTitleInput] = useState('');
  const [bookingTitleUpdating, setBookingTitleUpdating] = useState(false);
  const [bookingRangeUpdating, setBookingRangeUpdating] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [showExportConfirmDialog, setShowExportConfirmDialog] = useState(false);
  const [exportConsentChecked, setExportConsentChecked] = useState(false);
  const [showDisableTerraceDialog, setShowDisableTerraceDialog] = useState(false);
  
  // Spam alert settings
  const [spamAlertEnabled, setSpamAlertEnabled] = useState(true);
  const [spamAlertThreshold, setSpamAlertThreshold] = useState(20);
  const [spamAlertPeriodMinutes, setSpamAlertPeriodMinutes] = useState(60);
  const [spamSettingsUpdating, setSpamSettingsUpdating] = useState(false);
  
  // Large group contact settings
  const [largeGroupEmailEnabled, setLargeGroupEmailEnabled] = useState(false);
  const [largeGroupEmail, setLargeGroupEmail] = useState('');
  const [largeGroupContactMethod, setLargeGroupContactMethod] = useState<'email' | 'phone'>('email');
  const [largeGroupPhone, setLargeGroupPhone] = useState('');
  const [largeGroupMessage, setLargeGroupMessage] = useState('');
  const [largeGroupUpdating, setLargeGroupUpdating] = useState(false);

  const [formData, setFormData] = useState<EstablishmentSettingsUpdate>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Suisse',
    googleMapsUrl: '',
    googleBusinessUrl: '',
    websiteUrl: '',
    slug: '',
    tripadvisorReviewUrl: '',
    yelpReviewUrl: '',
    facebookReviewUrl: '',
    trustpilotReviewUrl: '',
    theforkReviewUrl: '',
    preferredReviewPlatform: 'google',
    bookingNotice: '',
  });

  // Generate slug from name
  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-') // Spaces to hyphens
      .replace(/-+/g, '-') // Multiple hyphens to single
      .replace(/^-|-$/g, ''); // Trim hyphens
  };

  // Use saved slug, but if it's "default", generate from name instead
  const savedSlugIsDefault = settings?.slug === 'default';
  const currentSlug = (settings?.slug && !savedSlugIsDefault) ? settings.slug : generateSlug(formData.name || '');
  const bookingUrl = currentSlug ? `https://piktable.ch/${currentSlug}` : '';

  const handleCopyLink = () => {
    if (!bookingUrl) return;
    navigator.clipboard.writeText(bookingUrl).then(() => {
      setLinkCopied(true);
      toast({
        title: 'Lien copié !',
        description: 'Le lien de réservation a été copié dans le presse-papier.',
      });
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // Get configured platforms for radio selection
  const configuredPlatforms = useMemo(() => {
    return ALL_REVIEW_PLATFORMS.filter(platform => {
      const value = formData[platform.field];
      return value && value.trim() !== '';
    });
  }, [formData]);

  // Sync form data with settings
  useEffect(() => {
    if (settings) {
      setFormData({
        name: settings.name || '',
        email: settings.email || '',
        phone: settings.phone || '',
        address: settings.address || '',
        city: settings.city || '',
        postalCode: settings.postalCode || '',
        country: settings.country || 'Suisse',
        googleMapsUrl: settings.googleMapsUrl || '',
        googleBusinessUrl: settings.googleBusinessUrl || '',
        websiteUrl: settings.websiteUrl || '',
        slug: settings.slug || '',
        tripadvisorReviewUrl: settings.tripadvisorReviewUrl || '',
        yelpReviewUrl: settings.yelpReviewUrl || '',
        facebookReviewUrl: settings.facebookReviewUrl || '',
        trustpilotReviewUrl: settings.trustpilotReviewUrl || '',
        theforkReviewUrl: settings.theforkReviewUrl || '',
        preferredReviewPlatform: settings.preferredReviewPlatform || 'google',
        bookingNotice: settings.bookingNotice || '',
      });
    }
  }, [settings]);

  // Sync reservation settings
  useEffect(() => {
    if (reservationSettings) {
      setMinPeople(reservationSettings.minPeople);
      setMaxPeople(reservationSettings.maxPeople);
      setMinDays(reservationSettings.minBookingDays);
      setMaxDays(reservationSettings.maxBookingDays);
      setMinMinutes(reservationSettings.minBookingMinutes);
      setAllowSameDayCancellation(reservationSettings.allowSameDayCancellation);
      setShowPhoneOnBlocked(reservationSettings.showPhoneOnBlockedActions);
      setShowEmailOnBlocked(reservationSettings.showEmailOnBlockedActions);
      setBlockedActionsPhoneInput(reservationSettings.blockedActionsPhone ?? '');
      setBlockedActionsEmailInput(reservationSettings.blockedActionsEmail ?? '');
      setBookingTitleInput(reservationSettings.bookingTitle ?? '');
      setLargeGroupEmailEnabled(reservationSettings.largeGroupEmailEnabled);
      setLargeGroupEmail(reservationSettings.largeGroupEmail || '');
      setLargeGroupContactMethod(reservationSettings.largeGroupContactMethod || 'email');
      setLargeGroupPhone(reservationSettings.largeGroupPhone || '');
      setLargeGroupMessage(reservationSettings.largeGroupMessage || '');
      setSameDayCutoffTime(reservationSettings.sameDayCutoffTime);
      setSameDayMode(reservationSettings.sameDayCutoffTime ? 'cutoff' : 'delay');
    }
  }, [reservationSettings]);

  // Fetch spam alert settings
  useEffect(() => {
    const fetchSpamSettings = async () => {
      if (!establishmentId) return;
      
      const { data, error } = await supabase
        .from('reservation_settings')
        .select('spam_alert_enabled, spam_alert_threshold, spam_alert_period_minutes')
        .eq('establishment_id', establishmentId)
        .single();
      
      if (!error && data) {
        setSpamAlertEnabled(data.spam_alert_enabled ?? true);
        setSpamAlertThreshold(data.spam_alert_threshold ?? 20);
        setSpamAlertPeriodMinutes(data.spam_alert_period_minutes ?? 60);
      }
    };
    
    fetchSpamSettings();
  }, [establishmentId]);

  const handleSpamSettingsSubmit = async () => {
    if (!reservationSettings) return;
    
    setSpamSettingsUpdating(true);
    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({
          spam_alert_enabled: spamAlertEnabled,
          spam_alert_threshold: spamAlertThreshold,
          spam_alert_period_minutes: spamAlertPeriodMinutes,
        })
        .eq('id', reservationSettings.id);
      
      if (error) throw error;
      
      toast({
        title: 'Paramètres d\'alerte mis à jour',
        description: spamAlertEnabled 
          ? `Alerte si plus de ${spamAlertThreshold} réservations en ${spamAlertPeriodMinutes} minutes.`
          : 'Alertes de sécurité désactivées.',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour les paramètres.',
        variant: 'destructive',
      });
    } finally {
      setSpamSettingsUpdating(false);
    }
  };

  // Redirect non-admin users
  useEffect(() => {
    if (role && role !== 'admin') {
      toast({
        title: 'Accès refusé',
        description: 'Seuls les administrateurs peuvent accéder à cette page.',
        variant: 'destructive',
      });
      navigate('/dashboard');
    }
  }, [role, navigate]);

  // Handle URL hash for tab navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['booking', 'config', 'info', 'reviews', 'data'];
    if (hash && validTabs.includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`#${value}`, { replace: true });
  };

  const handleChange = (field: keyof EstablishmentSettingsUpdate, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value || null }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Auto-generate slug from name if not already set in DB or if it's "default"
    const savedSlugIsValid = settings?.slug && settings.slug !== 'default';
    const slugToSave = savedSlugIsValid ? settings.slug : generateSlug(formData.name || '');
    updateSettings({ ...formData, slug: slugToSave });
  };

  const handlePeopleLimitsSubmit = async () => {
    if (minPeople > maxPeople) {
      toast({
        title: 'Erreur de validation',
        description: 'Le minimum ne peut pas être supérieur au maximum.',
        variant: 'destructive',
      });
      return;
    }
    if (minPeople < 1 || maxPeople > 100) {
      toast({
        title: 'Erreur de validation',
        description: 'Les valeurs doivent être entre 1 et 100.',
        variant: 'destructive',
      });
      return;
    }
    
    setPeopleLimitsUpdating(true);
    try {
      await updatePeopleLimits(minPeople, maxPeople);
      toast({
        title: 'Limites mises à jour',
        description: `Les réservations sont maintenant limitées de ${minPeople} à ${maxPeople} personnes.`,
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour les limites.',
        variant: 'destructive',
      });
    } finally {
      setPeopleLimitsUpdating(false);
    }
  };

  const handleBookingRangeSubmit = async () => {
    if (minDays < 0) {
      toast({
        title: 'Erreur de validation',
        description: 'Le minimum ne peut pas être négatif.',
        variant: 'destructive',
      });
      return;
    }
    if (maxDays < minDays) {
      toast({
        title: 'Erreur de validation',
        description: 'Le maximum doit être supérieur ou égal au minimum.',
        variant: 'destructive',
      });
      return;
    }
    if (maxDays > 365) {
      toast({
        title: 'Erreur de validation',
        description: 'Le maximum ne peut pas dépasser 365 jours.',
        variant: 'destructive',
      });
      return;
    }
    
    setBookingRangeUpdating(true);
    try {
      await updateBookingRange(minDays, maxDays);
      if (minDays === 0) {
        if (sameDayMode === 'cutoff') {
          await updateSameDayCutoffTime(sameDayCutoffTime);
          // Also reset minBookingMinutes when switching to cutoff mode
          await updateMinBookingMinutes(0);
        } else {
          await updateMinBookingMinutes(minMinutes);
          // Also reset cutoff time when switching to delay mode
          await updateSameDayCutoffTime(null);
        }
      }
      toast({
        title: 'Fenêtre de réservation mise à jour',
        description: `Les clients peuvent réserver entre J+${minDays} et J+${maxDays}.`,
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour la fenêtre de réservation.',
        variant: 'destructive',
      });
    } finally {
      setBookingRangeUpdating(false);
    }
  };

  const handleRequirePhoneChange = async (required: boolean) => {
    setRequirePhoneUpdating(true);
    try {
      await updateRequirePhone(required);
      toast({
        title: required ? 'Téléphone obligatoire activé' : 'Téléphone facultatif',
        description: required
          ? 'Le numéro est maintenant requis sur le lien public et le widget.'
          : 'Le numéro redevient facultatif pour les réservations en ligne.',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier ce réglage.',
        variant: 'destructive',
      });
    } finally {
      setRequirePhoneUpdating(false);
    }
  };

  const handleBookingTitleSave = async () => {
    setBookingTitleUpdating(true);
    try {
      await updateBookingTitle(bookingTitleInput);
      toast({
        title: 'Titre de réservation mis à jour',
        description: bookingTitleInput.trim()
          ? 'Le nouveau titre apparaît sur le lien public et le widget.'
          : 'Le titre par défaut est de nouveau utilisé.',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier le titre de réservation.',
        variant: 'destructive',
      });
    } finally {
      setBookingTitleUpdating(false);
    }
  };

  const handleDeleteAllData = async () => {
    setIsDeletingAll(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-all-data', {
        body: {}
      });

      if (error) throw error;

      toast({
        title: 'Données supprimées',
        description: 'Toutes les réservations et clients ont été supprimés avec succès.',
      });

    } catch (error) {
      logger.error('Erreur suppression des données:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer les données.',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingAll(false);
    }
  };

  if (isLoading || reservationSettingsLoading) {
    return <PiktableLoader message="Chargement des paramètres..." />;
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-card border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-serif font-bold">Paramètres</h1>
                <p className="text-sm text-muted-foreground">Gérez les informations et la configuration de votre établissement</p>
              </div>
            </div>
            {settings && (
              <PlanBadge 
                planType={settings.planType} 
                trialEndsAt={settings.trialEndsAt}
              />
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="w-full grid grid-cols-5 mb-6">
            <TabsTrigger value="booking" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Link2 className="w-4 h-4 hidden sm:block" />
              <span>Réservation</span>
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Settings className="w-4 h-4 hidden sm:block" />
              <span>Config</span>
            </TabsTrigger>
            <TabsTrigger value="info" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Building className="w-4 h-4 hidden sm:block" />
              <span>Établ.</span>
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Star className="w-4 h-4 hidden sm:block" />
              <span>Avis</span>
            </TabsTrigger>
            <TabsTrigger value="data" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Database className="w-4 h-4 hidden sm:block" />
              <span>Données</span>
            </TabsTrigger>
          </TabsList>

          {/* ============================================ */}
          {/* TAB 1: Réservation (Booking Link & Widget) */}
          {/* ============================================ */}
          <TabsContent value="booking" className="space-y-6">
            <Card className={currentSlug ? "border-green-200 dark:border-green-900" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Link2 className="w-5 h-5" />
                    Lien de réservation
                  </CardTitle>
                  {currentSlug && (
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 border-0">
                      <Check className="w-3 h-3 mr-1" />
                      Actif
                    </Badge>
                  )}
                </div>
                <CardDescription>
                  Partagez ce lien avec vos clients pour qu'ils puissent réserver
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {currentSlug ? (
                  <>
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <code className="text-lg font-mono font-semibold text-primary break-all">
                        {bookingUrl}
                      </code>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" onClick={handleCopyLink}>
                        {linkCopied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                        {linkCopied ? "Copié !" : "Copier le lien"}
                      </Button>
                      <Button type="button" variant="outline" asChild>
                        <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Ouvrir
                        </a>
                      </Button>
                    </div>
                  </>
                ) : (
                  <Alert>
                    <AlertDescription>
                      Renseignez le nom de votre établissement dans l'onglet "Établ." pour générer automatiquement votre lien de réservation.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {currentSlug && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Code className="w-5 h-5" />
                    Widget de réservation
                  </CardTitle>
                  <CardDescription>
                    Intégrez le formulaire de réservation directement sur votre site web
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <WidgetCodeGenerator slug={currentSlug} />
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Message pour les clients
                </CardTitle>
                <CardDescription>
                  Cette remarque s'affiche juste avant la confirmation de réservation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  id="bookingNotice"
                  value={formData.bookingNotice || ''}
                  onChange={(e) => handleChange('bookingNotice', e.target.value.slice(0, 500))}
                  placeholder="Ex: Nous avons 1 chaise bébé à disposition. Merci de nous indiquer si vous avez plus de 2 enfants inclus dans votre réservation."
                  className="min-h-[100px]"
                />
                <div className="flex justify-between items-center">
                  <p className="text-xs text-muted-foreground">
                    Ce message apparaîtra dans une encadré bleu informatif.
                  </p>
                  <span className={`text-xs ${(formData.bookingNotice?.length || 0) > 450 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    {formData.bookingNotice?.length || 0}/500
                  </span>
                </div>
                
                {formData.bookingNotice && formData.bookingNotice.trim() !== '' && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Aperçu :</Label>
                    <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
                    <AlertDescription className="text-blue-800 dark:text-blue-300 text-sm whitespace-pre-wrap">
                        {formData.bookingNotice}
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <Button type="submit" disabled={isUpdating} className="w-full">
                    {isUpdating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      'Sauvegarder le message'
                    )}
                  </Button>
                </form>

                <Separator className="my-4" />

                {/* Notes optionnelles (permanentes, datées ou hebdomadaires) */}
                <EstablishmentNoticesSettings establishmentId={settings?.id} />

                <Separator className="my-4" />

                {/* Question posée au client lors de la réservation */}
                <EstablishmentQuestionsSettings establishmentId={settings?.id} />

                <Separator className="my-4" />

                {/* Large group contact settings */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="large-group-toggle" className="text-sm font-medium">
                        Message pour les grands groupes
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Afficher un message invitant les clients à vous contacter pour les groupes dépassant {reservationSettings?.maxPeople || 20} personnes
                      </p>
                    </div>
                    <Switch
                      id="large-group-toggle"
                      checked={largeGroupEmailEnabled}
                      onCheckedChange={setLargeGroupEmailEnabled}
                    />
                  </div>
                  
                  {largeGroupEmailEnabled && (
                    <div className="space-y-4">
                      {/* Contact method selector */}
                      <div className="space-y-2">
                        <Label className="text-sm">Méthode de contact</Label>
                        <RadioGroup
                          value={largeGroupContactMethod}
                          onValueChange={(v) => setLargeGroupContactMethod(v as 'email' | 'phone')}
                          className="flex gap-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="email" id="contact-email" />
                            <Label htmlFor="contact-email" className="text-sm font-normal cursor-pointer">Email</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="phone" id="contact-phone" />
                            <Label htmlFor="contact-phone" className="text-sm font-normal cursor-pointer">Téléphone</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Dynamic input based on method */}
                      {largeGroupContactMethod === 'email' ? (
                        <div className="space-y-2">
                          <Label htmlFor="large-group-email" className="text-sm">
                            Email de contact
                          </Label>
                          <Input
                            id="large-group-email"
                            type="email"
                            value={largeGroupEmail}
                            onChange={(e) => setLargeGroupEmail(e.target.value)}
                            placeholder="groupes@votrerestaurant.ch"
                            className="max-w-md"
                          />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label htmlFor="large-group-phone" className="text-sm">
                            Numéro de téléphone
                          </Label>
                          <Input
                            id="large-group-phone"
                            type="tel"
                            value={largeGroupPhone}
                            onChange={(e) => setLargeGroupPhone(e.target.value)}
                            placeholder="+41 XX XXX XX XX"
                            className="max-w-md"
                          />
                        </div>
                      )}

                      {/* Custom message */}
                      <div className="space-y-2">
                        <Label htmlFor="large-group-message" className="text-sm">
                          Message personnalisé <span className="text-muted-foreground font-normal">(optionnel)</span>
                        </Label>
                        <Textarea
                          id="large-group-message"
                          value={largeGroupMessage}
                          onChange={(e) => setLargeGroupMessage(e.target.value)}
                          placeholder={largeGroupContactMethod === 'email' 
                            ? `Pour les groupes de plus de ${reservationSettings?.maxPeople || 20} personnes, merci de nous contacter par email.`
                            : `Pour les groupes de plus de ${reservationSettings?.maxPeople || 20} personnes, merci de nous appeler.`
                          }
                          className="max-w-md"
                          rows={3}
                        />
                        <p className="text-xs text-muted-foreground">
                          Laissez vide pour utiliser le message par défaut. Variables disponibles : {'{maxPeople}'}, {'{contact}'}
                        </p>
                      </div>

                      {/* Live preview */}
                      {((largeGroupContactMethod === 'email' && largeGroupEmail && largeGroupEmail.includes('@')) ||
                        (largeGroupContactMethod === 'phone' && largeGroupPhone)) && (
                        <div className="space-y-2 mt-3">
                          <Label className="text-xs text-muted-foreground">Aperçu sur la page de réservation :</Label>
                          <p className="text-sm text-muted-foreground">
                            {(() => {
                              const contact = largeGroupContactMethod === 'email' ? largeGroupEmail : largeGroupPhone;
                              const defaultMsg = largeGroupContactMethod === 'email'
                                ? `Pour les groupes de plus de ${reservationSettings?.maxPeople || 20} personnes, merci de nous contacter :`
                                : `Pour les groupes de plus de ${reservationSettings?.maxPeople || 20} personnes, merci de nous appeler :`;
                              const msg = largeGroupMessage 
                                ? largeGroupMessage.replace('{maxPeople}', String(reservationSettings?.maxPeople || 20)).replace('{contact}', contact)
                                : defaultMsg;
                              return msg;
                            })()}{' '}
                            <span className="text-primary underline">
                              {largeGroupContactMethod === 'email' ? largeGroupEmail : largeGroupPhone}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <Button 
                    onClick={async () => {
                      if (largeGroupEmailEnabled) {
                        if (largeGroupContactMethod === 'email' && (!largeGroupEmail || !largeGroupEmail.includes('@'))) {
                          toast({
                            title: 'Email invalide',
                            description: 'Veuillez entrer une adresse email valide.',
                            variant: 'destructive',
                          });
                          return;
                        }
                        if (largeGroupContactMethod === 'phone' && !largeGroupPhone) {
                          toast({
                            title: 'Téléphone manquant',
                            description: 'Veuillez entrer un numéro de téléphone.',
                            variant: 'destructive',
                          });
                          return;
                        }
                      }
                      setLargeGroupUpdating(true);
                      try {
                        await updateLargeGroupContact(
                          largeGroupEmailEnabled,
                          largeGroupContactMethod,
                          largeGroupEmail || null,
                          largeGroupPhone || null,
                          largeGroupMessage || null
                        );
                        toast({
                          title: 'Paramètres mis à jour',
                          description: largeGroupEmailEnabled 
                            ? 'Le message pour les grands groupes sera affiché.' 
                            : 'Le message pour les grands groupes a été désactivé.',
                        });
                      } catch (error) {
                        toast({
                          title: 'Erreur',
                          description: 'Impossible de mettre à jour les paramètres.',
                          variant: 'destructive',
                        });
                      } finally {
                        setLargeGroupUpdating(false);
                      }
                    }}
                    disabled={largeGroupUpdating}
                    variant="outline"
                    className="w-full"
                  >
                    {largeGroupUpdating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      'Sauvegarder les paramètres grands groupes'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================================ */}
          {/* TAB 2: Configuration (Booking Rules)       */}
          {/* ============================================ */}
          <TabsContent value="config" className="space-y-6">
            {reservationSettings && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heading className="h-5 w-5" />
                    Titre de la page de réservation
                  </CardTitle>
                  <CardDescription>
                    Personnalisez le titre affiché sur le lien public et le widget.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="booking-title">Titre personnalisé</Label>
                    <Input
                      id="booking-title"
                      value={bookingTitleInput}
                      onChange={(event) => setBookingTitleInput(event.target.value)}
                      maxLength={MAX_BOOKING_TITLE_LENGTH}
                      placeholder={resolveBookingTitle(null, formData.name)}
                      disabled={reservationSettingsLoading || bookingTitleUpdating}
                    />
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm text-muted-foreground">
                        Laissez vide pour utiliser « {resolveBookingTitle(null, formData.name)} ».
                      </p>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {bookingTitleInput.length}/{MAX_BOOKING_TITLE_LENGTH}
                      </span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={handleBookingTitleSave}
                    disabled={reservationSettingsLoading || bookingTitleUpdating || bookingTitleInput.trim() === (reservationSettings.bookingTitle ?? '')}
                  >
                    {bookingTitleUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Enregistrer le titre
                  </Button>
                </CardContent>
              </Card>
            )}

            {reservationSettings && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Informations demandées au client
                  </CardTitle>
                  <CardDescription>
                    Choisissez les coordonnées nécessaires pour réserver en ligne.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="require-phone">Téléphone obligatoire</Label>
                      <p className="text-sm text-muted-foreground">
                        Concerne uniquement le lien public et le widget. Les réservations saisies par l'équipe et les walk-ins restent flexibles.
                      </p>
                    </div>
                    <Switch
                      id="require-phone"
                      checked={reservationSettings.requirePhone}
                      onCheckedChange={handleRequirePhoneChange}
                      disabled={reservationSettingsLoading || requirePhoneUpdating}
                      aria-label="Exiger le téléphone pour les réservations en ligne"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Mode de réservation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Mode de réservation
                </CardTitle>
                <CardDescription>
                  Choisissez comment vos clients peuvent réserver
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reservationSettings && (
                  <BookingModeSettings
                    bookingMode={reservationSettings.bookingMode}
                    bookingIntervalMinutes={reservationSettings.bookingIntervalMinutes}
                    reservationDurationMinutes={reservationSettings.reservationDurationMinutes}
                    timeLimitType={reservationSettings.timeLimitType}
                    maxReservationsPerTime={reservationSettings.maxReservationsPerTime}
                    maxPeoplePerTime={reservationSettings.maxPeoplePerTime}
                    onUpdate={updateBookingMode}
                    disabled={reservationSettingsLoading}
                  />
                )}
                {reservationSettings && reservationSettings.bookingMode === 'time' && establishmentId && (
                  <div className="pt-4 border-t border-border/50">
                    <TimeSlotLimitsSettings
                      establishmentId={establishmentId}
                      timeSlots={(supabaseTimeSlots || []).map(ts => ({
                        id: ts.id || '',
                        start_time: ts.start,
                        end_time: ts.end,
                        available_days: ts.availableDays,
                        duration_override_minutes: ts.durationOverrideMinutes ?? null,
                      })).filter(ts => ts.id)}
                      bookingIntervalMinutes={reservationSettings.bookingIntervalMinutes}
                      reservationDurationMinutes={reservationSettings.reservationDurationMinutes}
                      disabled={reservationSettingsLoading}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Capacité */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Capacité par réservation
                </CardTitle>
                <CardDescription>
                  Nombre minimum et maximum de personnes par réservation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Minimum</Label>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0"
                        onClick={() => setMinPeople(Math.max(1, minPeople - 1))}
                        disabled={minPeople <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className="flex-1 text-center">
                        <span className="text-2xl font-semibold">{minPeople}</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0"
                        onClick={() => setMinPeople(Math.min(50, minPeople + 1))}
                        disabled={minPeople >= 50}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Maximum</Label>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0"
                        onClick={() => setMaxPeople(Math.max(1, maxPeople - 1))}
                        disabled={maxPeople <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className="flex-1 text-center">
                        <span className="text-2xl font-semibold">{maxPeople}</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0"
                        onClick={() => setMaxPeople(Math.min(100, maxPeople + 1))}
                        disabled={maxPeople >= 100}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {minPeople > maxPeople && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      Le minimum ne peut pas être supérieur au maximum.
                    </AlertDescription>
                  </Alert>
                )}
                
                <p className="text-sm text-muted-foreground">
                  Les clients ne pourront réserver que pour {minPeople} à {maxPeople} personnes.
                </p>
                
                <Button 
                  type="button" 
                  onClick={handlePeopleLimitsSubmit}
                  disabled={peopleLimitsUpdating || minPeople > maxPeople}
                  className="w-full"
                >
                  {peopleLimitsUpdating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Mise à jour...
                    </>
                  ) : (
                    'Sauvegarder'
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Enfants */}
            {reservationSettings && (
              <ChildrenSettings
                childrenEnabled={reservationSettings.childrenEnabled}
                childrenCapacityMode={reservationSettings.childrenCapacityMode}
                childrenHelpText={reservationSettings.childrenHelpText}
                strollerEnabled={reservationSettings.strollerEnabled}
                strollerHelpText={reservationSettings.strollerHelpText}
                onUpdate={updateChildrenSettings}
                onUpdateStroller={updateStrollerSettings}
                disabled={reservationSettingsLoading}
              />
            )}

            {/* Terrasse */}
            {reservationSettings && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-xl">🌿</span>
                    Réservation en terrasse
                  </CardTitle>
                  <CardDescription>
                    Permet à vos clients d'exprimer une préférence pour la terrasse
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="terraceEnabled">Activer la préférence terrasse</Label>
                      <p className="text-xs text-muted-foreground">
                        Activée : les clients choisissent intérieur ou terrasse. Désactivée : les tables terrasse ne sont attribuées que par l'équipe.
                      </p>
                    </div>
                    <Switch
                      id="terraceEnabled"
                      checked={reservationSettings.terraceEnabled}
                      onCheckedChange={async (checked) => {
                        if (!checked) {
                          setShowDisableTerraceDialog(true);
                          return;
                        }
                        try {
                          await updateTerraceSettings(true);
                          toast({
                            title: 'Terrasse activée',
                            description: 'Vos clients pourront exprimer une préférence pour la terrasse.',
                          });
                        } catch {
                          toast({ title: 'Erreur', variant: 'destructive' });
                        }
                      }}
                    />
                  </div>

                  {reservationSettings.terraceEnabled && (
                    <div className="space-y-4 pt-2 border-t">
                      <div className="space-y-2">
                        <Label htmlFor="terraceHelpText">Texte d'aide affiché aux clients</Label>
                        <Textarea
                          id="terraceHelpText"
                          defaultValue={reservationSettings.terraceHelpText}
                          placeholder="Souhaitez-vous être placé en terrasse ?"
                          rows={2}
                          onBlur={async (e) => {
                            const newText = e.target.value.trim();
                            if (newText && newText !== reservationSettings.terraceHelpText) {
                              try {
                                await updateTerraceSettings(true, newText);
                                toast({ title: 'Texte d\'aide mis à jour' });
                              } catch {
                                toast({ title: 'Erreur', variant: 'destructive' });
                              }
                            }
                          }}
                        />
                      </div>
                      <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
                        <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        <AlertDescription className="text-blue-800 dark:text-blue-300 text-xs">
                          N'oubliez pas de marquer vos tables terrasse dans la page{' '}
                          <a href="/dashboard/tables" className="underline font-medium">Gestion des tables</a>.
                          La préférence est une indication, pas une garantie — le message le précise au client.
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Zones réservables nommées */}
            {establishmentId && reservationSettings && (
              <ZoneManagementCard
                establishmentId={establishmentId}
                zonesEnabled={reservationSettings.zonesEnabled}
                zonesSelectionMode={reservationSettings.zonesSelectionMode}
                zonesHelpText={reservationSettings.zonesHelpText}
                zonesStrict={reservationSettings.zonesStrict}
                onUpdateSettings={updateZonesSettings}
              />
            )}

            {/* Consentements marketing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5" />
                  Consentements marketing
                </CardTitle>
                <CardDescription>
                  Choisissez quels opt-in afficher sur le formulaire de réservation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="emailMarketingEnabled">Opt-in e-mail</Label>
                    <p className="text-xs text-muted-foreground">
                      Afficher la case « Envoyez-moi des offres par e-mail »
                    </p>
                  </div>
                  <Switch
                    id="emailMarketingEnabled"
                    checked={reservationSettings.emailMarketingEnabled}
                    onCheckedChange={async (checked) => {
                      try {
                        await updateMarketingSettings(checked, reservationSettings.smsMarketingEnabled);
                        toast({ title: checked ? 'Opt-in e-mail activé' : 'Opt-in e-mail désactivé' });
                      } catch {
                        toast({ title: 'Erreur', variant: 'destructive' });
                      }
                    }}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="smsMarketingEnabled">Opt-in SMS</Label>
                    <p className="text-xs text-muted-foreground">
                      Afficher la case « Envoyez-moi des offres par SMS »
                    </p>
                  </div>
                  <Switch
                    id="smsMarketingEnabled"
                    checked={reservationSettings.smsMarketingEnabled}
                    onCheckedChange={async (checked) => {
                      try {
                        await updateMarketingSettings(reservationSettings.emailMarketingEnabled, checked);
                        toast({ title: checked ? 'Opt-in SMS activé' : 'Opt-in SMS désactivé' });
                      } catch {
                        toast({ title: 'Erreur', variant: 'destructive' });
                      }
                    }}
                  />
                </div>

                <Alert className="bg-muted/50 border-muted">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <AlertDescription className="text-muted-foreground text-xs">
                    Les consentements sont enregistrés avec horodatage pour la conformité nLPD. 
                    Une nouvelle réservation sans opt-in ne révoque pas un consentement existant.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            {/* Fenêtre de réservation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Fenêtre de réservation
                </CardTitle>
                <CardDescription>
                  Période pendant laquelle vos clients peuvent réserver
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="minDays" className="text-xs text-muted-foreground">Minimum (jours)</Label>
                    <div className="flex items-center gap-3">
                      <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={minDays <= 0} onClick={() => setMinDays(Math.max(0, minDays - 1))}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="text-2xl font-semibold w-12 text-center">{minDays}</span>
                      <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={minDays >= 365} onClick={() => setMinDays(Math.min(365, minDays + 1))}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Ex: 2 = réserver à partir de J+2
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxDays" className="text-xs text-muted-foreground">Maximum (jours)</Label>
                    <div className="flex items-center gap-3">
                      <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={maxDays <= minDays} onClick={() => setMaxDays(Math.max(minDays, maxDays - 1))}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="text-2xl font-semibold w-12 text-center">{maxDays}</span>
                      <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={maxDays >= 365} onClick={() => setMaxDays(Math.min(365, maxDays + 1))}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Ex: 30 = réserver jusqu'à J+30
                    </p>
                  </div>
                </div>

                {minDays === 0 && (
                  <div className="space-y-4">
                    <Label className="text-xs text-muted-foreground">Réservations du jour même</Label>
                    <RadioGroup
                      value={sameDayMode}
                      onValueChange={(value: 'delay' | 'cutoff') => {
                        setSameDayMode(value);
                        if (value === 'cutoff') {
                          setMinMinutes(0);
                          if (!sameDayCutoffTime) setSameDayCutoffTime('17:00:00');
                        } else {
                          setSameDayCutoffTime(null);
                        }
                      }}
                      className="space-y-3"
                    >
                      <div className="flex items-start space-x-3">
                        <RadioGroupItem value="delay" id="same-day-delay" className="mt-1" />
                        <div className="space-y-1 flex-1">
                          <Label htmlFor="same-day-delay" className="text-sm font-medium cursor-pointer">
                            Délai glissant
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Ex: pas de réservation dans les 2 heures
                          </p>
                          {sameDayMode === 'delay' && (
                            <Select
                              value={minMinutes.toString()}
                              onValueChange={(value) => setMinMinutes(parseInt(value))}
                            >
                              <SelectTrigger className="w-full mt-2">
                                <SelectValue placeholder="Sélectionner un délai" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">Aucun délai</SelectItem>
                                <SelectItem value="30">30 minutes</SelectItem>
                                <SelectItem value="60">1 heure</SelectItem>
                                <SelectItem value="90">1h30</SelectItem>
                                <SelectItem value="120">2 heures</SelectItem>
                                <SelectItem value="150">2h30</SelectItem>
                                <SelectItem value="180">3 heures</SelectItem>
                                <SelectItem value="210">3h30</SelectItem>
                                <SelectItem value="240">4 heures</SelectItem>
                                <SelectItem value="300">5 heures</SelectItem>
                                <SelectItem value="360">6 heures</SelectItem>
                                <SelectItem value="480">8 heures</SelectItem>
                                <SelectItem value="720">12 heures</SelectItem>
                                <SelectItem value="1440">24 heures</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <RadioGroupItem value="cutoff" id="same-day-cutoff" className="mt-1" />
                        <div className="space-y-1 flex-1">
                          <Label htmlFor="same-day-cutoff" className="text-sm font-medium cursor-pointer">
                            Heure limite fixe
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Ex: accepter les réservations du jour même jusqu'à 17h00
                          </p>
                          {sameDayMode === 'cutoff' && (
                            <Select
                              value={sameDayCutoffTime?.substring(0, 5) || '17:00'}
                              onValueChange={(value) => setSameDayCutoffTime(`${value}:00`)}
                            >
                              <SelectTrigger className="w-full mt-2">
                                <SelectValue placeholder="Heure limite" />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 29 }, (_, i) => {
                                  const hour = 8 + Math.floor(i / 2);
                                  const minutes = i % 2 === 0 ? '00' : '30';
                                  if (hour > 22 || (hour === 22 && minutes === '30')) return null;
                                  const time = `${hour.toString().padStart(2, '0')}:${minutes}`;
                                  return (
                                    <SelectItem key={time} value={time}>
                                      {time}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </RadioGroup>
                  </div>
                )}

                {maxDays < minDays && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      Le maximum doit être supérieur ou égal au minimum.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="p-3 bg-muted/50 rounded-lg border">
                  <p className="text-sm font-medium mb-1">Aperçu :</p>
                  <p className="text-xs text-muted-foreground">
                    Réservations de <strong>J+{minDays}</strong> à <strong>J+{maxDays}</strong>
                    {minDays === 0 && sameDayMode === 'delay' && minMinutes > 0 && (
                      <> (créneaux dans moins de <strong>{minMinutes >= 60 ? `${Math.floor(minMinutes / 60)}h${minMinutes % 60 > 0 ? (minMinutes % 60).toString().padStart(2, '0') : ''}` : `${minMinutes}min`}</strong> exclus)</>
                    )}
                    {minDays === 0 && sameDayMode === 'cutoff' && sameDayCutoffTime && (
                      <> (jour même accepté jusqu'à <strong>{sameDayCutoffTime.substring(0, 5)}</strong>)</>
                    )}
                  </p>
                </div>
                
                <Button 
                  type="button" 
                  onClick={handleBookingRangeSubmit}
                  disabled={bookingRangeUpdating || maxDays < minDays}
                  className="w-full"
                >
                  {bookingRangeUpdating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Mise à jour...
                    </>
                  ) : (
                    'Sauvegarder'
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Politique d'annulation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Politique d'annulation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="allowSameDayCancellation" className="text-sm font-medium">
                      Autoriser l'annulation le jour même
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Si désactivé, les clients devront vous contacter pour annuler
                    </p>
                  </div>
                  <Switch
                    id="allowSameDayCancellation"
                    checked={allowSameDayCancellation}
                    onCheckedChange={async (checked) => {
                      setAllowSameDayCancellation(checked);
                      try {
                        await updateAllowSameDayCancellation(checked);
                        toast({
                          title: checked ? "Annulation jour même activée" : "Annulation jour même désactivée",
                        });
                      } catch (error) {
                        setAllowSameDayCancellation(!checked);
                        toast({
                          title: 'Erreur',
                          description: 'Impossible de sauvegarder ce paramètre.',
                          variant: 'destructive',
                        });
                      }
                    }}
                    disabled={!reservationSettings}
                  />
                </div>
                {!allowSameDayCancellation && (
                  <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-100">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>
                      Vos clients devront vous contacter pour annuler. Configurez les coordonnées affichées dans la section <strong>« Coordonnées de secours pour vos clients »</strong> ci-dessous.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Coordonnées de secours */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Coordonnées de secours pour vos clients
                </CardTitle>
                <CardDescription className="space-y-2">
                  <span className="block">Affichées sur les pages publiques de réservation dans 4 cas :</span>
                  <ul className="list-disc list-inside space-y-0.5 ml-1">
                    <li>Modification d'une réservation le jour même <span className="text-muted-foreground">(toujours bloquée en ligne)</span></li>
                    <li>Annulation le jour même quand vous l'avez désactivée au-dessus</li>
                    <li>Lien de modification ou d'annulation expiré</li>
                    <li>Réservation déjà annulée</li>
                  </ul>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Téléphone */}
                <div className="space-y-3 rounded-md border bg-muted/30 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <Label htmlFor="showPhoneOnBlocked" className="text-sm font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Téléphone affiché
                    </Label>
                    <Switch
                      id="showPhoneOnBlocked"
                      checked={showPhoneOnBlocked}
                      onCheckedChange={async (checked) => {
                        const prev = showPhoneOnBlocked;
                        setShowPhoneOnBlocked(checked);
                        try {
                          await updateBlockedActionsContact(checked, showEmailOnBlocked, blockedActionsPhoneInput, blockedActionsEmailInput);
                          toast({ title: checked ? "Téléphone affiché" : "Téléphone masqué" });
                        } catch (error) {
                          setShowPhoneOnBlocked(prev);
                          toast({ title: 'Erreur', description: 'Impossible de sauvegarder.', variant: 'destructive' });
                        }
                      }}
                      disabled={!reservationSettings}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Input
                      type="tel"
                      placeholder={settings?.phone || '+41 22 000 00 00'}
                      value={blockedActionsPhoneInput}
                      onChange={(e) => setBlockedActionsPhoneInput(e.target.value)}
                      onBlur={async () => {
                        const current = reservationSettings?.blockedActionsPhone ?? '';
                        if (blockedActionsPhoneInput.trim() === current.trim()) return;
                        try {
                          await updateBlockedActionsContact(showPhoneOnBlocked, showEmailOnBlocked, blockedActionsPhoneInput, blockedActionsEmailInput);
                          toast({ title: 'Téléphone enregistré' });
                        } catch (error) {
                          toast({ title: 'Erreur', description: 'Impossible de sauvegarder.', variant: 'destructive' });
                        }
                      }}
                      disabled={!reservationSettings || !showPhoneOnBlocked}
                    />
                    <p className="text-xs text-muted-foreground">
                      {!showPhoneOnBlocked
                        ? "Activez le switch pour configurer un numéro."
                        : blockedActionsPhoneInput.trim()
                          ? "Ce numéro sera affiché à vos clients."
                          : settings?.phone
                            ? `Vide → fallback sur le téléphone de votre établissement (${settings.phone}).`
                            : "Aucun numéro disponible — saisissez-en un ou ajoutez-le dans la fiche établissement."}
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-3 rounded-md border bg-muted/30 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <Label htmlFor="showEmailOnBlocked" className="text-sm font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email affiché
                    </Label>
                    <Switch
                      id="showEmailOnBlocked"
                      checked={showEmailOnBlocked}
                      onCheckedChange={async (checked) => {
                        const prev = showEmailOnBlocked;
                        setShowEmailOnBlocked(checked);
                        try {
                          await updateBlockedActionsContact(showPhoneOnBlocked, checked, blockedActionsPhoneInput, blockedActionsEmailInput);
                          toast({ title: checked ? "Email affiché" : "Email masqué" });
                        } catch (error) {
                          setShowEmailOnBlocked(prev);
                          toast({ title: 'Erreur', description: 'Impossible de sauvegarder.', variant: 'destructive' });
                        }
                      }}
                      disabled={!reservationSettings}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Input
                      type="email"
                      placeholder={settings?.email || 'contact@exemple.ch'}
                      value={blockedActionsEmailInput}
                      onChange={(e) => setBlockedActionsEmailInput(e.target.value)}
                      onBlur={async () => {
                        const current = reservationSettings?.blockedActionsEmail ?? '';
                        if (blockedActionsEmailInput.trim() === current.trim()) return;
                        try {
                          await updateBlockedActionsContact(showPhoneOnBlocked, showEmailOnBlocked, blockedActionsPhoneInput, blockedActionsEmailInput);
                          toast({ title: 'Email enregistré' });
                        } catch (error) {
                          toast({ title: 'Erreur', description: 'Impossible de sauvegarder.', variant: 'destructive' });
                        }
                      }}
                      disabled={!reservationSettings || !showEmailOnBlocked}
                    />
                    <p className="text-xs text-muted-foreground">
                      {!showEmailOnBlocked
                        ? "Activez le switch pour configurer un email."
                        : blockedActionsEmailInput.trim()
                          ? "Cette adresse sera affichée à vos clients."
                          : settings?.email
                            ? `Vide → fallback sur l'email de votre établissement (${settings.email}).`
                            : "Aucune adresse disponible — saisissez-en une ou ajoutez-la dans la fiche établissement."}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Alertes de sécurité
                </CardTitle>
                <CardDescription>
                  Recevez une alerte en cas d'activité inhabituelle sur vos réservations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="spamAlertEnabled" className="text-sm font-medium">
                      Activer les alertes
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Vous serez notifié si un grand nombre de réservations est créé rapidement
                    </p>
                  </div>
                  <Switch
                    id="spamAlertEnabled"
                    checked={spamAlertEnabled}
                    onCheckedChange={setSpamAlertEnabled}
                  />
                </div>

                {spamAlertEnabled && (
                  <>
                    <Separator />
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="spamThreshold" className="text-xs text-muted-foreground">
                          Seuil (réservations)
                        </Label>
                        <div className="flex items-center gap-3">
                          <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={spamAlertThreshold <= 5} onClick={() => setSpamAlertThreshold(Math.max(5, spamAlertThreshold - 1))}>
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="text-2xl font-semibold w-12 text-center">{spamAlertThreshold}</span>
                          <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={spamAlertThreshold >= 100} onClick={() => setSpamAlertThreshold(Math.min(100, spamAlertThreshold + 1))}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="spamPeriod" className="text-xs text-muted-foreground">
                          Période (minutes)
                        </Label>
                        <div className="flex items-center gap-3">
                          <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={spamAlertPeriodMinutes <= 10} onClick={() => setSpamAlertPeriodMinutes(Math.max(10, spamAlertPeriodMinutes - 5))}>
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="text-2xl font-semibold w-12 text-center">{spamAlertPeriodMinutes}</span>
                          <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={spamAlertPeriodMinutes >= 180} onClick={() => setSpamAlertPeriodMinutes(Math.min(180, spamAlertPeriodMinutes + 5))}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 bg-muted/50 rounded-lg border">
                      <p className="text-sm font-medium mb-1">Configuration actuelle :</p>
                      <p className="text-xs text-muted-foreground">
                        Alerte si plus de <strong>{spamAlertThreshold} réservations</strong> en <strong>{spamAlertPeriodMinutes} minutes</strong>
                      </p>
                    </div>
                  </>
                )}

                <Button 
                  type="button" 
                  onClick={handleSpamSettingsSubmit}
                  disabled={spamSettingsUpdating}
                  className="w-full"
                >
                  {spamSettingsUpdating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Mise à jour...
                    </>
                  ) : (
                    'Sauvegarder'
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================================ */}
          {/* TAB 3: Établissement (General Info)        */}
          {/* ============================================ */}
          <TabsContent value="info" className="space-y-6">
            <form onSubmit={handleSubmit}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="w-5 h-5" />
                    Informations générales
                  </CardTitle>
                  <CardDescription>
                    Identité et coordonnées de votre établissement
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom de l'établissement *</Label>
                    <Input
                      id="name"
                      value={formData.name || ''}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Mon restaurant"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Ce nom apparaît dans tous les emails envoyés à vos clients
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="websiteUrl" className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      Site web
                    </Label>
                    <Input
                      id="websiteUrl"
                      type="url"
                      value={formData.websiteUrl || ''}
                      onChange={(e) => handleChange('websiteUrl', e.target.value)}
                      placeholder="https://www.monrestaurant.ch"
                    />
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="contact@monrestaurant.ch"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        Téléphone
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone || ''}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="+41 XX XXX XX XX"
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <Label className="flex items-center gap-2 text-sm font-medium">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      Adresse
                    </Label>
                    <div className="space-y-2">
                      <Input
                        id="address"
                        value={formData.address || ''}
                        onChange={(e) => handleChange('address', e.target.value)}
                        placeholder="Rue de la Gare 12"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="postalCode" className="text-xs text-muted-foreground">Code postal</Label>
                        <Input
                          id="postalCode"
                          value={formData.postalCode || ''}
                          onChange={(e) => handleChange('postalCode', e.target.value)}
                          placeholder="1000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="city" className="text-xs text-muted-foreground">Ville</Label>
                        <Input
                          id="city"
                          value={formData.city || ''}
                          onChange={(e) => handleChange('city', e.target.value)}
                          placeholder="Lausanne"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-xs text-muted-foreground">Pays</Label>
                      <Input
                        id="country"
                        value={formData.country || ''}
                        onChange={(e) => handleChange('country', e.target.value)}
                        placeholder="Suisse"
                      />
                    </div>
                  </div>

                  <Button type="submit" disabled={isUpdating} className="w-full">
                    {isUpdating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      'Sauvegarder'
                    )}
                  </Button>
                </CardContent>
              </Card>
            </form>
          </TabsContent>

          {/* ============================================ */}
          {/* TAB 4: Avis clients (Reviews)              */}
          {/* ============================================ */}
          <TabsContent value="reviews" className="space-y-6">
            <form onSubmit={handleSubmit}>
              {/* Google Business - Principal */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-xl">{GOOGLE_PLATFORM.icon}</span>
                    Google Business
                  </CardTitle>
                  <CardDescription>
                    Plateforme recommandée pour la visibilité locale
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="googleBusinessUrl">Lien d'avis Google</Label>
                    <Input
                      id="googleBusinessUrl"
                      type="url"
                      value={formData.googleBusinessUrl || ''}
                      onChange={(e) => handleChange('googleBusinessUrl', e.target.value)}
                      placeholder="https://g.page/r/..."
                    />
                  </div>
                  <GoogleReviewLinkHelper compact />
                </CardContent>
              </Card>

              {/* Google Maps */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Lien Google Maps
                  </CardTitle>
                  <CardDescription>
                    Pour afficher votre localisation aux clients
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Input
                    id="googleMapsUrl"
                    type="url"
                    value={formData.googleMapsUrl || ''}
                    onChange={(e) => handleChange('googleMapsUrl', e.target.value)}
                    placeholder="https://maps.google.com/..."
                  />
                </CardContent>
              </Card>

              {/* Autres plateformes - Accordéon */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5" />
                    Autres plateformes d'avis
                  </CardTitle>
                  <CardDescription>
                    Ajoutez vos liens vers d'autres plateformes (optionnel)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="other-platforms" className="border-none">
                      <AccordionTrigger className="text-sm hover:no-underline py-2">
                        Afficher les autres plateformes
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <TooltipProvider>
                          <div className="space-y-4">
                            {OTHER_REVIEW_PLATFORMS.map((platform) => (
                              <div key={platform.id} className="space-y-2">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 flex items-center justify-center text-lg shrink-0">
                                    {platform.icon}
                                  </div>
                                  <div className="flex-1 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <Label htmlFor={platform.field} className="text-sm">{platform.name}</Label>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent side="right" className="max-w-xs">
                                          <p className="text-xs">{platform.helpText}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </div>
                                    <Input
                                      id={platform.field}
                                      type="url"
                                      value={(formData[platform.field] as string) || ''}
                                      onChange={(e) => handleChange(platform.field, e.target.value)}
                                      placeholder={`https://${platform.id === 'thefork' ? 'thefork.com' : platform.id + '.com'}/...`}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </TooltipProvider>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>

              {/* Plateforme préférée */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Plateforme préférée pour les emails</CardTitle>
                  <CardDescription>
                    Ce lien sera utilisé dans les emails de suivi après visite
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {configuredPlatforms.length > 0 ? (
                    <>
                      <RadioGroup
                        value={formData.preferredReviewPlatform || 'google'}
                        onValueChange={(value) => handleChange('preferredReviewPlatform', value)}
                        className="grid grid-cols-2 gap-2"
                      >
                        {configuredPlatforms.map((platform) => (
                          <div key={platform.id} className="flex items-center space-x-2">
                            <RadioGroupItem value={platform.id} id={`preferred-${platform.id}`} />
                            <Label htmlFor={`preferred-${platform.id}`} className="flex items-center gap-2 cursor-pointer">
                              <span>{platform.icon}</span>
                              <span className="text-sm">{platform.name}</span>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                      <div className="bg-muted/50 rounded-lg p-3 mt-2">
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <Check className="w-3.5 h-3.5 text-green-600" />
                          Ce lien sera utilisé dans les emails de suivi.
                        </p>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground italic py-2">
                      Ajoutez au moins un lien d'avis ci-dessus pour sélectionner votre plateforme préférée.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Button type="submit" disabled={isUpdating} className="w-full">
                {isUpdating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  'Sauvegarder les avis'
                )}
              </Button>
            </form>
          </TabsContent>

          {/* ============================================ */}
          {/* TAB 5: Données (Export & Danger Zone)      */}
          {/* ============================================ */}
          <TabsContent value="data" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Export des données
                </CardTitle>
                <CardDescription>
                  Téléchargez vos données de réservation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium">Exporter les réservations</p>
                    <p className="text-sm text-muted-foreground">
                      Format CSV, compatible avec Excel et Google Sheets.
                    </p>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={() => setShowExportConfirmDialog(true)}
                    disabled={dataLoading || supabaseReservations.length === 0}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Exporter
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-destructive/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <Trash2 className="w-5 h-5" />
                  Zone de danger
                </CardTitle>
                <CardDescription>
                  Actions irréversibles - à utiliser avec précaution
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium">Effacer toutes les données</p>
                    <p className="text-sm text-muted-foreground">
                      Supprime toutes les réservations et tous les clients. Irréversible.
                    </p>
                  </div>
                  <Button 
                    variant="destructive" 
                    onClick={() => setShowDeleteAllDialog(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Effacer
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Delete All Data Dialog */}
        <DeleteAllDataDialog
          open={showDeleteAllDialog}
          onOpenChange={setShowDeleteAllDialog}
          onConfirm={handleDeleteAllData}
          isDeleting={isDeletingAll}
        />

        {/* Export CSV Confirmation Dialog with nLPD Warning */}
        <AlertDialog 
          open={showExportConfirmDialog} 
          onOpenChange={(open) => {
            setShowExportConfirmDialog(open);
            if (!open) setExportConsentChecked(false);
          }}
        >
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                <ShieldAlert className="w-5 h-5" />
                Avertissement - Données personnelles
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4 pt-2">
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-sm text-foreground font-medium mb-2">
                      Ce fichier contient des données personnelles sensibles :
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                      <li>Noms et prénoms des clients</li>
                      <li>Adresses email</li>
                      <li>Numéros de téléphone</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground flex items-center gap-2">
                      📋 Vos obligations légales (nLPD - Art. 6, 8, 25)
                    </p>
                    <p className="text-xs text-muted-foreground">
                      En vertu de la Loi fédérale sur la protection des données (nLPD), vous êtes responsable de :
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1 ml-4 list-disc">
                      <li>Utiliser ces données uniquement pour la finalité prévue</li>
                      <li>Garantir la sécurité et la confidentialité des données</li>
                      <li>Ne pas transmettre ces données à des tiers sans base légale</li>
                      <li>Supprimer les données dès qu'elles ne sont plus nécessaires</li>
                    </ul>
                  </div>

                  <div className="border-t pt-3">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={exportConsentChecked}
                        onChange={(e) => setExportConsentChecked(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      />
                      <span className="text-xs text-foreground leading-relaxed group-hover:text-primary transition-colors">
                        Je confirme avoir pris connaissance de mes obligations et m'engage à traiter ces données conformément à la nLPD.
                      </span>
                    </label>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction 
                disabled={!exportConsentChecked}
                className={cn(
                  !exportConsentChecked && "opacity-50 cursor-not-allowed"
                )}
                onClick={async () => {
                  if (!exportConsentChecked) return;
                  
                  // Log the export for nLPD compliance
                  try {
                    const { data: { user } } = await supabase.auth.getUser();
                    if (user && establishmentId) {
                      await supabase.from('export_logs').insert({
                        user_id: user.id,
                        establishment_id: establishmentId,
                        export_type: 'reservations_csv',
                        record_count: supabaseReservations.length,
                      });
                    }
                  } catch (error) {
                    logger.error('Failed to log export:', error);
                  }
                  
                  // Transform reservations to export format
                  const exportData: ReservationExportData[] = supabaseReservations.map((r, index) => ({
                    id: index + 1,
                    supabaseId: r.id,
                    time: r.startTime,
                    name: formatCustomerName(r.customerFirstName, r.customerLastName),
                    persons: r.numberOfPeople,
                    numberOfChildren: r.numberOfChildren || 0,
                    tableId: r.tableId || undefined,
                    combinedTableId: r.combinedTableId || undefined,
                    status: r.status as "planifié" | "arrivé" | "annulé" | "en_attente",
                    phone: r.customerPhone || '',
                    email: r.customerEmail || undefined,
                    reservationDate: new Date(r.date),
                    createdAt: new Date(r.createdAt || new Date()),
                    notes: r.notes || undefined,
                    internalNotes: r.internalNotes || undefined,
                    source: r.source || undefined,
                  }));
                  
                  exportReservationsToCSV(
                    exportData,
                    supabaseTables,
                    supabaseCombinedTables
                  );
                  setShowExportConfirmDialog(false);
                  setExportConsentChecked(false);
                  
                  toast({
                    title: 'Export réussi',
                    description: `${exportData.length} réservations ont été exportées.`,
                  });
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      {establishmentId && (
        <DisableTerraceDialog
          open={showDisableTerraceDialog}
          onOpenChange={setShowDisableTerraceDialog}
          establishmentId={establishmentId}
          onConfirm={async () => {
            try {
              await updateTerraceSettings(false);
              toast({
                title: 'Terrasse désactivée',
                description: 'La préférence terrasse est désactivée.',
              });
            } catch {
              toast({ title: 'Erreur', variant: 'destructive' });
            }
          }}
        />
      )}
      </div>
    </div>
  );
}
